import type { Zone } from '@seek/audience-zones';
import type { Locale } from '@seek/ca-myprofile-utils';
import { createForm, type RenderParams } from '@seek/forms-ui';
import { ProfileAutosuggest } from '@seek/profile-edit-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Actions, Button, Card, Dropdown, Stack } from 'braid-design-system';
import { useCallback } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import {
  type CountryInformation,
  type RightToWorkOption,
  type RightToWorkType2Input,
  SourceExperience,
} from 'src/types/graphql';

import translations from './.vocab';

interface ComponentProps {
  onSubmit: (arg0: {
    country: CountryInformation;
    type2: RightToWorkType2Input;
  }) => void;
  onCancel: () => void;
  onSubmitFail?: (errors: string[]) => void;
  scrollToTopOnMobile?: boolean;
  locale?: Locale;
  inputRef?: React.RefObject<HTMLInputElement>;
  zone: Zone;
  existingCountryCodes: string[];
}

export interface AutosuggestItem {
  text: string;
  value?: string;
  country?: CountryInformation;
  options?: RightToWorkOption[];
}

export interface FormValues {
  country: AutosuggestItem;
  rightToWorkOption: string;
}

const initialValues: FormValues = {
  country: { text: '' },
  rightToWorkOption: '',
};

type RightToWorkFormProps = ComponentProps & RenderParams<FormValues>;

const RIGHT_TO_WORK_FORM = 'RIGHT_TO_WORK_FORM';

const { FormProvider, useField } = createForm<FormValues>(RIGHT_TO_WORK_FORM, {
  formType: 'braid',
});

const Form = ({
  onSubmit,
  onCancel,
  handleSubmit,
  inputRef,
  locale,
  zone,
  existingCountryCodes,
}: RightToWorkFormProps) => {
  const { t } = useTranslations(translations);

  const countryField = useField({
    id: 'country',
    ...rules.rightToWorkCountryName({
      existingCountryCodes,
    }),
  });
  const inputCountryCode = countryField.value.country?.countryCode;

  const rightToWorkOptionsField = useField({
    id: 'rightToWorkOption',
    ...rules.rightToWorkOption(),
  });

  const dropdownRightToWorkOptions = countryField.value.options;

  const isDuplicateCountry =
    inputCountryCode !== undefined &&
    existingCountryCodes.includes(inputCountryCode);

  const showDropdown = dropdownRightToWorkOptions && !isDuplicateCountry;

  const onSubmitFn = useCallback(
    () =>
      handleSubmit((formValues) => {
        const { country: countryInput } = formValues.country;
        const { rightToWorkOption: rightToWorkOptionInput } = formValues;

        if (
          countryInput &&
          dropdownRightToWorkOptions &&
          rightToWorkOptionInput
        ) {
          const selectedRightToWorkOption = dropdownRightToWorkOptions.find(
            (rightToWorkOption) =>
              rightToWorkOption.id === rightToWorkOptionInput,
          );

          if (selectedRightToWorkOption) {
            onSubmit({
              country: countryInput,
              type2: selectedRightToWorkOption,
            });
          }
        }
      }),
    [dropdownRightToWorkOptions, handleSubmit, onSubmit],
  );

  return (
    <form onSubmit={onSubmitFn()} autoComplete="off">
      <Card rounded>
        <Stack space="large">
          <Stack space="small">
            <ProfileAutosuggest
              id="right-to-work-country-autosuggest"
              label={t('Add country or region')}
              asset="RightToWorkOptions"
              placeholder={t('Enter country or region')}
              queryVariables={{ profileField: 'RightToWork' }}
              locale={locale}
              showNoResultsMessage={true}
              inputRef={inputRef}
              rightToWorkOptionsProps={{
                zone,
                source: SourceExperience.Profile,
              }}
              data={{ automation: 'add-rtw-country-autosuggest' }}
              {...countryField}
            />
            {showDropdown ? (
              <Dropdown
                id="right-to-work-options"
                label={t('Select right to work')}
                placeholder={t('Please select')}
                data={{ automation: 'add-rtw-country-dropdown' }}
                {...rightToWorkOptionsField}
              >
                {dropdownRightToWorkOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.description}
                  </option>
                ))}
              </Dropdown>
            ) : null}
          </Stack>

          <Actions>
            <Button
              type="submit"
              variant="solid"
              data={{ automation: 'add-rtw-country-submit-button' }}
            >
              {t('Add')}
            </Button>
            <Button variant="transparent" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </Actions>
        </Stack>
      </Card>
    </form>
  );
};

export const RightToWorkForm = (props: ComponentProps) => {
  const { language } = useAppConfig();

  return (
    <FormProvider initialValues={initialValues} language={language}>
      {(formProps) => <Form {...formProps} {...props} />}
    </FormProvider>
  );
};
